/**
 * Before and after of one move-in execution, as it was FROZEN WHEN IT RAN (D11a).
 *
 * THE ONLY SOURCE IS `address_change`. It is the snapshot the move-in writer
 * stored in the same commit as the address it wrote — the address the change
 * closed, the address it left, the move-in date it used, and its own verdict.
 * Nothing here reads the tenant's current address, an address-book row or a
 * tenant event: those are edited in place and would describe today, not the
 * day of the run.
 *
 * THREE HONEST STATES, AND NO FOURTH.
 *   - `changed: true` — what was there (or that nothing was) and what replaced
 *     it, valid from the move-in date.
 *   - `changed: false` — "Keine Änderung notwendig", with the address exactly
 *     as it was stored. That spelling is the record: the writer found it, kept
 *     it and froze it; it is not re-cased or compared against anything.
 *   - `null` — nothing was stored for this run (a run from before D11a, a skip,
 *     a failure, a tenant deleted since). That is said as such. It is NEVER
 *     "Keine Änderung", which would be a claim about a run nobody recorded.
 */
import React from "react";
import { ArrowDown, ArrowRight } from "lucide-react";

import { formatDateOnly } from "../../../utils/datetime";
import { automationRunAddressLines } from "./automationRunLabels";

function AddressBlock({ title, address, emptyText }) {
  const lines = automationRunAddressLines(address);
  return (
    <div className="min-w-0 rounded-[10px] border border-border bg-card px-3 py-2.5">
      <p className="m-0 text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </p>
      {lines.length ? (
        <address className="mt-1 break-words text-[12.5px] not-italic text-foreground">
          {/* `pre-wrap`: the stored spelling is the record, down to a double
              space — HTML would otherwise collapse it on screen. */}
          {lines.map((line, i) => (
            <span key={`${i}-${line}`} className="block whitespace-pre-wrap">
              {line}
            </span>
          ))}
        </address>
      ) : (
        <p className="m-0 mt-1 text-[12.5px] italic text-muted-foreground">{emptyText}</p>
      )}
    </div>
  );
}

function DateLine({ label, value }) {
  if (!value) return null;
  return (
    <p className="m-0 text-[12px] text-muted-foreground">
      {label}{" "}
      <time dateTime={value} className="font-semibold text-foreground">
        {formatDateOnly(value)}
      </time>
    </p>
  );
}

export default function AutomationRunAddressChange({ change }) {
  if (!change) {
    return (
      <p className="m-0 text-[12.5px] text-muted-foreground">
        Vorher/Nachher wurde für diese Ausführung nicht gespeichert.
      </p>
    );
  }

  if (!change.changed) {
    return (
      <div className="flex flex-col gap-2">
        <p className="m-0 text-[12.5px] font-semibold text-foreground">
          Keine Änderung notwendig
        </p>
        <p className="m-0 text-[12px] text-muted-foreground">
          Die Adresse war bereits eingetragen und wurde unverändert gelassen.
        </p>
        <AddressBlock
          title="Gespeicherte Adresse"
          address={change.before || change.after}
          emptyText="Keine Adresse gespeichert"
        />
        <DateLine label="Einzugsdatum" value={change.valid_from} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <p className="m-0 text-[12.5px] font-semibold text-foreground">Adresse übernommen</p>
      <div className="grid grid-cols-1 items-center gap-2 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]">
        <AddressBlock
          title="Vorher"
          address={change.before}
          emptyText="Keine vorherige Adresse gespeichert"
        />
        <span className="flex justify-center text-muted-foreground" aria-hidden="true">
          <ArrowDown className="h-4 w-4 sm:hidden" />
          <ArrowRight className="hidden h-4 w-4 sm:block" />
        </span>
        <AddressBlock title="Nachher" address={change.after} emptyText="Keine Adresse gespeichert" />
      </div>
      <DateLine label="Gültig ab" value={change.valid_from} />
    </div>
  );
}
